/* eslint-disable */
/**
 *
 * UploadMedia
 *
 */

import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import classNames from 'classnames';
import { useTranslation } from "react-i18next";

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';

import './style.scss';

import reducer from './reducer';
import saga from './saga';
import {
  makeSelectUploadedFile,
  makeSelectLoading,
  makeSelectError,
  makeSelectUploadedFileType,
} from './selectors';
import { uploadMediaFile, flushState } from './actions';

// antd component
import { Button, Upload, message } from 'antd';

// assets
import { ArrowRightOutlined } from '@ant-design/icons';
import CustomIcons from 'components/CustomIcons';

// Helper
import { resizeFile, scrubFileName } from 'utils/uploadHelper';

export function UploadMedia({
  media,
  showCamera,
  maxCount,
  description,
  // props action
  uploadChange,
  setProgress,
  // states
  loading,
  error,
  uploadedFile,
  uploadFileType,
  // container actions
  uploadMediaFile,
  flushState,
  // default props
  children,
  className,
  ...rest
}) {
  useInjectReducer({ key: 'uploadMedia', reducer });
  useInjectSaga({ key: 'uploadMedia', saga });

  const { t } = useTranslation();

  const mainClassNames = classNames('proof-option-upload', className);

  const [fileList, setFileList] = useState([]);

  // Clearing the state after unmounting a component
  useEffect(() => {
    return () => {
      // Anything in here is fired on component unmount.
      flushState();
    };
  }, []);

  // When file is getting back to the server, we refresh the list + the parent
  useEffect(() => {
    if (uploadedFile) {
      if (uploadedFile.hasOwnProperty('url')) {
        !!setProgress && setProgress(true);
        if (media && media?.length > 0 && maxCount > 1) {
          media?.map(item => {
            if (item !== uploadedFile.url) {
              !!uploadChange && uploadChange([...media, uploadedFile.url]);
            }
          });
        } else {
          !!uploadChange && uploadChange([uploadedFile.url]);
        }
      } else {
        !!setProgress && setProgress(false);
      }
    }
  }, [uploadedFile]);

  error &&
    message.error(t("errors.somethingWrongTryAgain"), 2);

  // when file remove, we upload the filelist + the parent
  const fileOnRemove = file => {
    let newFileList = [];
    let newContent = [];
    for (let i = 0; i < fileList.length; i++) {
      if (file.uid !== fileList[i].uid) {
        newFileList.push(fileList[i]);
        newContent.push(fileList[i].url);
      }
    }
    setFileList(newFileList);
    uploadChange(newContent);
  };

  // Upload and Compress File
  const handleUpload = ({ onSuccess, onError, file, onProgress }) => {
    const formPayload = new FormData();

    const type = file.type.indexOf('image') > 0 ? 'image' : 'document';

    if (type === 'image') {
      // compress uploaded file
      resizeFile(file)
        // need to wait the image are compress
        .then(compressFile => {
          // change Blob to File
          let tempCompressFile = new File(
            [compressFile],
            scrubFileName(compressFile.name),
          );

          /**
           * Check if previous files have same name
           * if yes, add timestamp to the current file name
           */
          fileList.length > 0 &&
            fileList.map(propFile => {
              if (propFile.name === tempCompressFile.name) {
                tempCompressFile = new File(
                  [tempCompressFile],
                  scrubFileName(
                    `${tempCompressFile.lastModified}_${tempCompressFile.name}`,
                  ),
                );
              }
            });

          // add uploading file to array of all files
          tempCompressFile.status = 'uploading';
          setFileList([...fileList, tempCompressFile]);

          formPayload.append('file', tempCompressFile);

          !loading && uploadMediaFile(formPayload, type);
        })
        .catch(err => {
          message.error(t("errors.somethingWrongTryAgain"), 2);
          onError(err);
        });
    }

    if (type === 'document' || (type === 'audio' || type === 'video')) {
      // change Blob to File
      let tempCompressFile = new File([file], scrubFileName(file.name));

      !tempCompressFile &&
        (message.error(t("errors.somethingWrongTryAgain"), 2),
        onError(t("errors.somethingWrongTryAgain")));

      // add uploading file to array of all files
      tempCompressFile.status = 'uploading';
      setFileList([...fileList, tempCompressFile]);

      formPayload.append('file', tempCompressFile);

      !loading && uploadMediaFile(formPayload, type);
    }
  };

  // render function
  return (
    <div
      className={mainClassNames}
    >
      <Upload
        multiple={false}
        maxCount={maxCount}
        customRequest={handleUpload}
        className={`proof-upload-filed ${loading ? 'loading' : ''} ${
          error ? 'error' : ''
        }`}
      >
        {children || (
          <Button className="proof-option">
            <CustomIcons className="proof-option__icon" type="proof-document" />
            <div className="proof-option__description">
              {!!description &&description || t("inputs.uploadingMedia")}
            </div>
            <ArrowRightOutlined className="proof-option__arrow" />
          </Button>
        )}
      </Upload>
    </div>
  );
}

UploadMedia.defaultProps = {
  maxCount: 1
};
UploadMedia.propTypes = {
  dispatch: PropTypes.func.isRequired,
  // props
  uploadChange: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  uploadedFile: makeSelectUploadedFile(),
  uploadFileType: makeSelectUploadedFileType(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    uploadMediaFile: (file, fileType) => dispatch(uploadMediaFile(file, fileType)),
    flushState: () => dispatch(flushState()),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(UploadMedia);
